import {
  GroupRuleValues,
  PlanRulesChange,
  PlanRulesDraft,
  PlanRulesGroup,
  PlanRulesHistoryItem,
  PlanRulesPlan,
  PlanRulesView,
  PublishPlanRulesRequest,
} from '../../../types/planRules';
import {
  FREE_PLAN_NAME,
  GEO_LABELS,
  HISTORY_COLUMN_LABELS,
  HISTORY_TABLE_LABELS,
  WEIGHTS_LABELS,
  WEIGHTS_STRICTNESS,
} from './premium.constants';

export const ruleKey = (planId: number, otherId: number) => `${planId}:${otherId}`;

export const isFreePlan = (plan: PlanRulesPlan) => plan.name === FREE_PLAN_NAME;

export const planLabel = (plan: PlanRulesPlan) => (isFreePlan(plan) ? `${plan.name} (free)` : plan.name);

/** Rules for a plan that had none: unlimited, any weights, any region. */
export const OPEN_GROUP_RULE: GroupRuleValues = {
  portfolioLimit: null,
  recommendationLimit: null,
  recommendationCappedByPortfolios: false,
  maxFunds: null,
  weightsRule: 'ANY',
  allowedFundGeoCategoryId: null,
};

export function toDraft(view: PlanRulesView): PlanRulesDraft {
  const groupRules: PlanRulesDraft['groupRules'] = {};
  const serviceRules: PlanRulesDraft['serviceRules'] = {};

  view.groupRules.forEach((rule) => {
    groupRules[ruleKey(rule.planId, rule.groupId)] = {
      portfolioLimit: rule.portfolioLimit,
      recommendationLimit: rule.recommendationLimit,
      recommendationCappedByPortfolios: rule.recommendationCappedByPortfolios,
      maxFunds: rule.maxFunds,
      weightsRule: rule.weightsRule,
      allowedFundGeoCategoryId: rule.allowedFundGeoCategoryId,
    };
  });
  view.serviceRules.forEach(({ planId, serviceId, limit }) => {
    serviceRules[ruleKey(planId, serviceId)] = { limit };
  });

  return {
    plans: view.plans.map((plan) => ({ ...plan })),
    groups: view.groups.map((group) => ({ ...group, productTypeIds: [...group.productTypeIds] })),
    groupRules,
    serviceRules,
  };
}

/** A limit in plain words: "1 portfolio", "∞ Unlimited", "🔒 Not allowed". */
export function limitText(value: number | null | undefined, noun: string) {
  if (value === null || value === undefined) {
    return '∞ Unlimited';
  }

  if (value === 0) {
    return '🔒 Not allowed';
  }

  return `${value} ${noun}${value === 1 ? '' : 's'}`;
}

export const geoText = (value: number | null) => GEO_LABELS[String(value)] ?? `Region #${value}`;

const onOff = (value: boolean) => (value ? 'On' : 'Off');
const feeText = (value: boolean) => (value ? 'Charge' : "Don't charge");
const sameList = (left: number[], right: number[]) =>
  left.length === right.length && left.every((id) => right.includes(id));

function limitWarning(where: string, before: number | null, after: number | null, freePlan: boolean) {
  if (freePlan && after === null) {
    return {
      text: `"Unlimited" on the free plan (${where}): every free user gets this with no limit.`,
      highRisk: true,
    };
  }

  if (before !== null && after !== null && after < before) {
    return { text: `${where}: lowered. Users above the new limit keep what they have, but can't add more.` };
  }

  return undefined;
}

/**
 * Every difference between what is saved and the draft, in plain words, with a warning when the change is risky.
 * Feeds the review popup, the "changed" marks and the unpublished-changes count.
 */
export function buildChanges(
  saved: PlanRulesDraft,
  draft: PlanRulesDraft,
  services: PlanRulesView['services'],
  productName: (id: number) => string,
): PlanRulesChange[] {
  const changes: PlanRulesChange[] = [];

  draft.plans.forEach((plan) => {
    const before = saved.plans.find((item) => item.id === plan.id);

    if (!before) {
      return;
    }

    const where = `${planLabel(plan)} · Plan totals`;
    const free = isFreePlan(plan);

    if (plan.portfolioLimit !== before.portfolioLimit) {
      changes.push({
        key: `plan.${plan.id}.portfolioLimit`,
        where: `${where} · Portfolios`,
        from: limitText(before.portfolioLimit, 'portfolio'),
        to: limitText(plan.portfolioLimit, 'portfolio'),
        warning: limitWarning(`${where} · Portfolios`, before.portfolioLimit, plan.portfolioLimit, free),
      });
    }

    if (plan.recommendationLimit !== before.recommendationLimit) {
      changes.push({
        key: `plan.${plan.id}.recommendationLimit`,
        where: `${where} · Saved recommendations`,
        from: limitText(before.recommendationLimit, 'recommendation'),
        to: limitText(plan.recommendationLimit, 'recommendation'),
        warning: limitWarning(
          `${where} · Saved recommendations`,
          before.recommendationLimit,
          plan.recommendationLimit,
          free,
        ),
      });
    }

    if (plan.recommendationCappedByPortfolios !== before.recommendationCappedByPortfolios) {
      changes.push({
        key: `plan.${plan.id}.recommendationCappedByPortfolios`,
        where: `${where} · Count recommendations as slots`,
        from: onOff(before.recommendationCappedByPortfolios),
        to: onOff(plan.recommendationCappedByPortfolios),
      });
    }

    if (plan.managementFee !== before.managementFee) {
      changes.push({
        key: `plan.${plan.id}.managementFee`,
        where: `${where} · Management fee`,
        from: feeText(before.managementFee),
        to: feeText(plan.managementFee),
      });
    }

    const roboWhere = `${planLabel(plan)} · Robo Advice`;

    if (plan.roboAdvicePortfolioLimit !== before.roboAdvicePortfolioLimit) {
      changes.push({
        key: `plan.${plan.id}.roboAdvicePortfolioLimit`,
        where: `${roboWhere} · Portfolios`,
        from: limitText(before.roboAdvicePortfolioLimit, 'portfolio'),
        to: limitText(plan.roboAdvicePortfolioLimit, 'portfolio'),
        warning: limitWarning(
          `${roboWhere} · Portfolios`,
          before.roboAdvicePortfolioLimit,
          plan.roboAdvicePortfolioLimit,
          free,
        ),
      });
    }

    if (plan.roboAdviceWeightsRule !== before.roboAdviceWeightsRule) {
      changes.push({
        key: `plan.${plan.id}.roboAdviceWeightsRule`,
        where: `${roboWhere} · Fund weights`,
        from: WEIGHTS_LABELS[before.roboAdviceWeightsRule],
        to: WEIGHTS_LABELS[plan.roboAdviceWeightsRule],
        warning:
          WEIGHTS_STRICTNESS[plan.roboAdviceWeightsRule] > WEIGHTS_STRICTNESS[before.roboAdviceWeightsRule]
            ? {
                text: `${roboWhere}: stricter weights. Users may be blocked when saving or opening a Robo Advice portfolio.`,
              }
            : undefined,
      });
    }
  });

  draft.groups.forEach((group) => {
    const before = saved.groups.find((item) => item.id === group.id);

    if (!before) {
      return;
    }

    if (group.active !== before.active) {
      changes.push({
        key: `group.${group.id}.active`,
        where: `${group.name} · Rules switched on`,
        from: onOff(before.active),
        to: onOff(group.active),
        warning: group.active
          ? undefined
          : {
              text: `${group.name}: rules switched off. These products will have no limits for anyone.`,
              highRisk: true,
            },
      });
    }

    if (!sameList(group.productTypeIds, before.productTypeIds)) {
      group.productTypeIds
        .filter((id) => !before.productTypeIds.includes(id))
        .forEach((id) => {
          changes.push({
            key: `group.${group.id}.products.+${id}`,
            where: `${group.name} · Products`,
            from: '—',
            to: `+ ${productName(id)}`,
          });
        });
      before.productTypeIds
        .filter((id) => !group.productTypeIds.includes(id))
        .forEach((id) => {
          changes.push({
            key: `group.${group.id}.products.-${id}`,
            where: `${group.name} · Products`,
            from: productName(id),
            to: 'removed',
            warning: group.productTypeIds.length
              ? undefined
              : { text: `${group.name}: no products left. This group will apply to nothing.` },
          });
        });
    }
  });

  draft.plans.forEach((plan) => {
    const free = isFreePlan(plan);

    draft.groups.forEach((group) => {
      const key = ruleKey(plan.id, group.id);
      const after = draft.groupRules[key];
      const before = saved.groupRules[key];
      const where = `${planLabel(plan)} · ${group.name}`;

      if (!before && !after) {
        return;
      }

      if (!before) {
        changes.push({ key: `rule.${key}.added`, where, from: 'No rules', to: 'Rules added' });

        return;
      }

      if (!after) {
        changes.push({
          key: `rule.${key}.removed`,
          where,
          from: 'Rules',
          to: 'No rules (open to everyone)',
          warning: { text: `${where}: no rules left. These products become open with no limits.`, highRisk: true },
        });

        return;
      }

      if (after.portfolioLimit !== before.portfolioLimit) {
        changes.push({
          key: `rule.${key}.portfolioLimit`,
          where: `${where} · Portfolios`,
          from: limitText(before.portfolioLimit, 'portfolio'),
          to: limitText(after.portfolioLimit, 'portfolio'),
          warning: limitWarning(`${where} · Portfolios`, before.portfolioLimit, after.portfolioLimit, free),
        });
      }

      if (after.recommendationLimit !== before.recommendationLimit) {
        changes.push({
          key: `rule.${key}.recommendationLimit`,
          where: `${where} · Saved recommendations`,
          from: limitText(before.recommendationLimit, 'recommendation'),
          to: limitText(after.recommendationLimit, 'recommendation'),
          warning: limitWarning(
            `${where} · Saved recommendations`,
            before.recommendationLimit,
            after.recommendationLimit,
            free,
          ),
        });
      }

      if (after.recommendationCappedByPortfolios !== before.recommendationCappedByPortfolios) {
        changes.push({
          key: `rule.${key}.recommendationCappedByPortfolios`,
          where: `${where} · Count recommendations as slots`,
          from: onOff(before.recommendationCappedByPortfolios),
          to: onOff(after.recommendationCappedByPortfolios),
        });
      }

      if (after.maxFunds !== before.maxFunds) {
        const stricter = after.maxFunds !== null && (before.maxFunds === null || after.maxFunds < before.maxFunds);

        changes.push({
          key: `rule.${key}.maxFunds`,
          where: `${where} · Funds in one portfolio`,
          from: limitText(before.maxFunds, 'fund'),
          to: limitText(after.maxFunds, 'fund'),
          warning: stricter
            ? { text: `${where}: fewer funds. Users holding more may be blocked from adding money.` }
            : undefined,
        });
      }

      if (after.weightsRule !== before.weightsRule) {
        changes.push({
          key: `rule.${key}.weightsRule`,
          where: `${where} · Fund weights`,
          from: WEIGHTS_LABELS[before.weightsRule],
          to: WEIGHTS_LABELS[after.weightsRule],
          warning:
            WEIGHTS_STRICTNESS[after.weightsRule] > WEIGHTS_STRICTNESS[before.weightsRule]
              ? { text: `${where}: stricter weights. Users may be blocked from adding money to existing portfolios.` }
              : undefined,
        });
      }

      if (after.allowedFundGeoCategoryId !== before.allowedFundGeoCategoryId) {
        changes.push({
          key: `rule.${key}.allowedFundGeoCategoryId`,
          where: `${where} · Funds from`,
          from: geoText(before.allowedFundGeoCategoryId),
          to: geoText(after.allowedFundGeoCategoryId),
          warning:
            after.allowedFundGeoCategoryId !== null
              ? {
                  text: `${where}: ${geoText(
                    after.allowedFundGeoCategoryId,
                  )}. Users holding other funds can't add money.`,
                }
              : undefined,
        });
      }
    });

    services.forEach((service) => {
      const key = ruleKey(plan.id, service.id);
      const included = Boolean(draft.serviceRules[key]);

      if (included !== Boolean(saved.serviceRules[key])) {
        changes.push({
          key: `service.${key}`,
          where: `${planLabel(plan)} · ${service.name}`,
          from: included ? 'Not included' : 'Included',
          to: included ? 'Included' : 'Not included',
        });
      }
    });
  });

  return changes;
}

/**
 * Plan-level warnings that are not about one change: the free plan allowing more than a paid plan, and a Robo Advice
 * limit the plan total can never reach (the total stops the user first).
 */
export function buildPlanWarnings(draft: PlanRulesDraft) {
  const roboAboveTotal = draft.plans
    .filter(
      (plan) =>
        plan.portfolioLimit !== null &&
        plan.roboAdvicePortfolioLimit !== null &&
        plan.roboAdvicePortfolioLimit > plan.portfolioLimit,
    )
    .map((plan) => ({
      text: `${planLabel(plan)}: Robo Advice allows ${limitText(
        plan.roboAdvicePortfolioLimit,
        'portfolio',
      )}, but the plan total is ${limitText(plan.portfolioLimit, 'portfolio')}. The total stops users first.`,
    }));

  const free = draft.plans.find(isFreePlan);

  if (!free) {
    return roboAboveTotal;
  }

  const freeAbovePaid = draft.plans
    .filter((plan) => !isFreePlan(plan))
    .filter(
      (plan) =>
        plan.portfolioLimit !== null && (free.portfolioLimit === null || free.portfolioLimit > plan.portfolioLimit),
    )
    .map((plan) => ({ text: `${free.name} now allows more portfolios than ${plan.name}. Is that right?` }));

  return [...freeAbovePaid, ...roboAboveTotal];
}

/** The publish body: only what changed. Group rules are sent whole, so a missing field never means "unlimited". */
export function buildPublishRequest(
  saved: PlanRulesDraft,
  draft: PlanRulesDraft,
  services: PlanRulesView['services'],
  version: number,
  note: string,
): PublishPlanRulesRequest {
  const plans = draft.plans
    .map((plan) => {
      // A plan not in `saved` can't happen (plans are never added here); comparing it with itself sends nothing.
      const before = saved.plans.find((item) => item.id === plan.id) ?? plan;
      const changed: NonNullable<PublishPlanRulesRequest['plans']>[number] = { planId: plan.id };

      (
        [
          'portfolioLimit',
          'recommendationLimit',
          'recommendationCappedByPortfolios',
          'managementFee',
          'roboAdvicePortfolioLimit',
          'roboAdviceWeightsRule',
        ] as const
      ).forEach((field) => {
        if (plan[field] !== before[field]) {
          Object.assign(changed, { [field]: plan[field] });
        }
      });

      return changed;
    })
    .filter((change) => Object.keys(change).length > 1);

  const groups = draft.groups
    .map((group) => {
      // New groups are created right away (not in a draft), so every group is in `saved`.
      const before = saved.groups.find((item) => item.id === group.id) ?? group;

      return {
        groupId: group.id,
        ...(group.active !== before.active ? { active: group.active } : {}),
        ...(group.name !== before.name ? { name: group.name } : {}),
        ...(!sameList(group.productTypeIds, before.productTypeIds) ? { productTypeIds: group.productTypeIds } : {}),
      };
    })
    .filter((change) => Object.keys(change).length > 1);

  const groupRules: NonNullable<PublishPlanRulesRequest['groupRules']> = [];
  const serviceRules: NonNullable<PublishPlanRulesRequest['serviceRules']> = [];

  draft.plans.forEach((plan) => {
    draft.groups.forEach((group) => {
      const key = ruleKey(plan.id, group.id);
      const after = draft.groupRules[key];
      const before = saved.groupRules[key];

      if (!after && before) {
        groupRules.push({ planId: plan.id, groupId: group.id, remove: true });
      } else if (after && JSON.stringify(after) !== JSON.stringify(before)) {
        groupRules.push({ planId: plan.id, groupId: group.id, ...after });
      }
    });

    services.forEach((service) => {
      const key = ruleKey(plan.id, service.id);
      const after = draft.serviceRules[key];
      const before = saved.serviceRules[key];

      if (Boolean(after) !== Boolean(before) || (after && before && after.limit !== before.limit)) {
        serviceRules.push({
          planId: plan.id,
          serviceId: service.id,
          included: Boolean(after),
          limit: after?.limit ?? null,
        });
      }
    });
  });

  return {
    basedOnVersion: version,
    ...(note ? { note } : {}),
    ...(plans.length ? { plans } : {}),
    ...(groups.length ? { groups } : {}),
    ...(groupRules.length ? { groupRules } : {}),
    ...(serviceRules.length ? { serviceRules } : {}),
  };
}

/** "What a free user sees" for one group, written from the free plan's rules (and the first paid plan's). */
export function freeUserSentence(draft: PlanRulesDraft, group: PlanRulesGroup) {
  const free = draft.plans.find(isFreePlan);
  const paid = draft.plans.find((plan) => !isFreePlan(plan));
  const rule = free ? draft.groupRules[ruleKey(free.id, group.id)] : undefined;

  if (!group.active) {
    return 'Rules are off: everyone can open these products with no limits.';
  }

  if (!rule) {
    return 'Free users have no rules here: they can open these products with no limits.';
  }

  if (rule.portfolioLimit === 0) {
    return `Free users can't open ${group.name}. They see the upgrade screen.`;
  }

  const parts = [
    rule.portfolioLimit === null
      ? `can open as many ${group.name} portfolios as they want`
      : `can open ${rule.portfolioLimit} ${group.name} portfolio${rule.portfolioLimit === 1 ? '' : 's'}`,
  ];

  if (rule.maxFunds !== null) {
    parts.push(`with up to ${rule.maxFunds} fund${rule.maxFunds === 1 ? '' : 's'}`);
  }

  parts.push(
    { TEMPLATE: 'keeping the template weights', EQUAL: 'split equally', ANY: 'with any weights' }[rule.weightsRule],
  );

  if (rule.allowedFundGeoCategoryId !== null) {
    parts.push(`from ${geoText(rule.allowedFundGeoCategoryId).replace(' only', '')} funds only`);
  }

  const paidRule = paid ? draft.groupRules[ruleKey(paid.id, group.id)] : undefined;
  const unlocks: string[] = [];

  if (paidRule) {
    if (paidRule.portfolioLimit !== rule.portfolioLimit) {
      unlocks.push(paidRule.portfolioLimit === null ? 'unlimited portfolios' : `${paidRule.portfolioLimit} portfolios`);
    }

    if (paidRule.maxFunds !== rule.maxFunds) {
      unlocks.push(paidRule.maxFunds === null ? 'any number of funds' : `up to ${paidRule.maxFunds} funds`);
    }

    if (paidRule.weightsRule === 'ANY' && rule.weightsRule !== 'ANY') {
      unlocks.push('any weights');
    }
  }

  return `Free users ${parts.join(', ')}.${unlocks.length ? ` Premium unlocks ${unlocks.join(', ')}.` : ''}`;
}

/** One history row in plain words, using the current rules to name plans, groups and services. */
export function historyRowText(item: PlanRulesHistoryItem, view: PlanRulesView, productName: (id: number) => string) {
  const parse = (value: string | null): unknown => (value === null ? undefined : JSON.parse(value));
  const column = item.columnName ?? '';
  const planName = (id: number) => {
    const plan = view.plans.find((item2) => item2.id === id);

    return plan ? planLabel(plan) : `Plan #${id}`;
  };
  const groupName = (id: number) => view.groups.find((group) => group.id === id)?.name ?? `Group #${id}`;

  let where = HISTORY_TABLE_LABELS[item.tableName] ?? item.tableName;

  if (item.tableName === 'plans') {
    where = `${planName(item.rowId)} · Plan totals`;
  } else if (item.tableName === 'plans_portfolio_feature') {
    where = groupName(item.rowId);
  } else if (item.tableName === 'plans_portfolio_feature_details') {
    const rule = view.groupRules.find((groupRule) => groupRule.id === item.rowId);

    where = rule ? `${planName(rule.planId)} · ${groupName(rule.groupId)}` : `Group rules #${item.rowId}`;
  } else if (item.tableName === 'plans_service_feature_details') {
    const rule = view.serviceRules.find((serviceRule) => serviceRule.id === item.rowId);
    const service = rule && view.services.find((item2) => item2.id === rule.serviceId);

    where = rule && service ? `${planName(rule.planId)} · ${service.name}` : `Service #${item.rowId}`;
  }

  const show = (raw: string | null) => {
    if (raw === null) {
      return item.tableName === 'plans_service_feature_details' ? 'Not included' : '—';
    }

    const value = parse(raw);

    switch (column) {
      case 'portfolioLimit':
      case 'roboAdvicePortfolioLimit':
        return limitText(value as number | null, 'portfolio');
      case 'recommendationLimit':
        return limitText(value as number | null, 'recommendation');
      case 'maxFunds':
        return limitText(value as number | null, 'fund');
      case 'weightsRule':
      case 'roboAdviceWeightsRule':
        return WEIGHTS_LABELS[value as keyof typeof WEIGHTS_LABELS] ?? String(value);
      case 'allowedFundGeoCategoryId':
        return geoText(value as number | null);
      case 'productTypeId':
        return productName(value as number);
      case 'plansPortfolioFeatureId':
        return groupName(value as number);
      case 'limit':
        return value === null ? 'Included (unlimited)' : `Included (limit ${value})`;
      default:
        return typeof value === 'boolean' ? onOff(value) : String(value);
    }
  };

  return {
    where: `${where} · ${HISTORY_COLUMN_LABELS[column] ?? column}`,
    from: show(item.oldValue),
    to: show(item.newValue),
  };
}
